import React, { useState, useEffect } from 'react';
import { useLayout } from '../../contexts/LayoutContext';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './StatusSummaryCard.css';

// Campo Aperto Icon (Weather Station)
const CampoApertoIcon = ({ size = 16, color = '#5A7A3A' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none">
    <line x1="12" y1="19" x2="12" y2="22" stroke={color} strokeWidth="2.5" strokeLinecap="round"></line>
    <rect x="7" y="11" width="10" height="7" rx="1.5" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></rect>
    <circle cx="12" cy="8" r="2.5" fill="none" stroke={color} strokeWidth="2"></circle>
    <line x1="12" y1="10.5" x2="12" y2="11" stroke={color} strokeWidth="2" strokeLinecap="round"></line>
    <path d="M12 8 L14 6 M12 8 L10 6" stroke={color} strokeWidth="2" strokeLinecap="round"></path>
    <path d="M8 8 Q12 4 16 8" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.7"></path>
    <rect x="9" y="13" width="6" height="3" rx="0.5" fill={color} opacity="0.2"></rect>
    <line x1="10" y1="14.5" x2="14" y2="14.5" stroke={color} strokeWidth="1" strokeLinecap="round"></line>
  </svg>
);

// Retro Modulo Icon (Solar Panel Station)
const RetroModuloIcon = ({ size = 16, color = '#5A7A3A' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none">
    <line x1="12" y1="19" x2="12" y2="22" stroke={color} strokeWidth="2.5" strokeLinecap="round"></line>
    <rect x="5" y="4" width="14" height="8" rx="1" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></rect>
    <line x1="5" y1="7" x2="19" y2="7" stroke={color} strokeWidth="1.5" strokeLinecap="round"></line>
    <line x1="5" y1="10" x2="19" y2="10" stroke={color} strokeWidth="1.5" strokeLinecap="round"></line>
    <line x1="9" y1="4" x2="9" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round"></line>
    <line x1="15" y1="4" x2="15" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round"></line>
    <rect x="8" y="12" width="8" height="6" rx="1" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></rect>
    <circle cx="12" cy="15" r="1.5" fill={color} opacity="0.6"></circle>
  </svg>
);

export function StatusSummaryCard({ farm, counts }) {
  const { t } = useTranslation();
  const { sidebarCollapsed } = useLayout();
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Auto-collapse on map interactions
  useEffect(() => {
    let timer = null;
    
    const handleMapMove = () => {
      // Clear existing timer
      if (timer) {
        clearTimeout(timer);
      }
      
      // Set new timer to collapse after 2 seconds of inactivity
      timer = setTimeout(() => {
        setIsCollapsed(true);
      }, 2000);
    };
    
    const handleStationClick = () => {
      // Collapse immediately when station is clicked
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      setIsCollapsed(true);
    };
    
    // Listen for map move events
    window.addEventListener('map:move', handleMapMove);
    window.addEventListener('map:station-clicked', handleStationClick);
    
    return () => {
      window.removeEventListener('map:move', handleMapMove);
      window.removeEventListener('map:station-clicked', handleStationClick);
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);
  
  // Get icon colors based on status
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return '#5A7A3A'; // Green
      case 'warning':
        return '#8A7A4A'; // Yellow/Orange
      case 'inactive':
        return '#8A4A3A'; // Red
      default:
        return '#5A7A3A';
    }
  };
  
  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };
  
  // Collapsed view - just a button
  if (isCollapsed) {
    return (
      <button
        className={`status-summary-card status-summary-card--collapsed ${sidebarCollapsed ? 'status-summary-card--collapsed-sidebar' : ''}`}
        onClick={handleToggle}
        title={t('map.legend.show')}
      >
        <Info size={18} />
        <span>{t('map.legend.show')}</span>
      </button>
    );
  }
  
  return (
    <div className={`status-summary-card ${sidebarCollapsed ? 'status-summary-card--collapsed-sidebar' : ''}`}>
      <h3 className="status-summary-card__title">
        {t('map.statusSummary.title', { farm: farm.name })}
      </h3>
      
      <div className="status-summary-card__content">
        {/* Total */}
        <div className="status-summary-card__section">
          <div className="status-summary-card__stat status-summary-card__stat--total">
            <div className="status-summary-card__total-number">
              {counts.total}
            </div>
            <div>
              <div className="status-summary-card__label">{t('stations.stats.total')}</div>
            </div>
          </div>
        </div>

        {/* Campo Aperto Section */}
        <div className="status-summary-card__section">
          <div className="status-summary-card__section-header">
            <CampoApertoIcon size={16} color="#5A7A3A" />
            <span className="status-summary-card__section-title">{t('stations.types.campo_aperto')}</span>
            <span className="status-summary-card__section-count">({counts.campoAperto?.total || 0})</span>
          </div>
          <div className="status-summary-card__grid">
            <div className="status-summary-card__stat">
              <div className="status-summary-card__icon status-summary-card__icon--active">
                <CampoApertoIcon size={16} color={getStatusColor('active')} />
              </div>
              <div>
                <div className="status-summary-card__label">{t('stations.status.active')}</div>
                <div className="status-summary-card__value status-summary-card__value--active">
                  {counts.campoAperto?.active || 0}
                </div>
              </div>
            </div>
            <div className="status-summary-card__stat">
              <div className="status-summary-card__icon status-summary-card__icon--warning">
                <CampoApertoIcon size={16} color={getStatusColor('warning')} />
              </div>
              <div>
                <div className="status-summary-card__label">{t('stations.status.warning')}</div>
                <div className="status-summary-card__value status-summary-card__value--warning">
                  {counts.campoAperto?.warning || 0}
                </div>
              </div>
            </div>
            <div className="status-summary-card__stat">
              <div className="status-summary-card__icon status-summary-card__icon--inactive">
                <CampoApertoIcon size={16} color={getStatusColor('inactive')} />
              </div>
              <div>
                <div className="status-summary-card__label">{t('stations.status.inactive')}</div>
                <div className="status-summary-card__value status-summary-card__value--inactive">
                  {counts.campoAperto?.inactive || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Retro Modulo Section */}
        <div className="status-summary-card__section">
          <div className="status-summary-card__section-header">
            <RetroModuloIcon size={16} color="#5A7A3A" />
            <span className="status-summary-card__section-title">{t('stations.types.retro_modulo')}</span>
            <span className="status-summary-card__section-count">({counts.retroModulo?.total || 0})</span>
          </div>
          <div className="status-summary-card__grid">
            <div className="status-summary-card__stat">
              <div className="status-summary-card__icon status-summary-card__icon--active">
                <RetroModuloIcon size={16} color={getStatusColor('active')} />
              </div>
              <div>
                <div className="status-summary-card__label">{t('stations.status.active')}</div>
                <div className="status-summary-card__value status-summary-card__value--active">
                  {counts.retroModulo?.active || 0}
                </div>
              </div>
            </div>
            <div className="status-summary-card__stat">
              <div className="status-summary-card__icon status-summary-card__icon--warning">
                <RetroModuloIcon size={16} color={getStatusColor('warning')} />
              </div>
              <div>
                <div className="status-summary-card__label">{t('stations.status.warning')}</div>
                <div className="status-summary-card__value status-summary-card__value--warning">
                  {counts.retroModulo?.warning || 0}
                </div>
              </div>
            </div>
            <div className="status-summary-card__stat">
              <div className="status-summary-card__icon status-summary-card__icon--inactive">
                <RetroModuloIcon size={16} color={getStatusColor('inactive')} />
              </div>
              <div>
                <div className="status-summary-card__label">{t('stations.status.inactive')}</div>
                <div className="status-summary-card__value status-summary-card__value--inactive">
                  {counts.retroModulo?.inactive || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Collapse button */}
      <button
        className="status-summary-card__collapse-btn"
        onClick={handleToggle}
        title={t('map.legend.hide')}
        aria-label={t('map.legend.hide')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m18 6-12 12"></path>
          <path d="m6 6 12 12"></path>
        </svg>
      </button>
    </div>
  );
}